import { useState, useContext } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  Button,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  FiberManualRecord,
  ChevronRight,
  ChevronLeft,
} from "@material-ui/icons";
import FiberManualRecordOutlinedIcon from "@material-ui/icons/FiberManualRecordOutlined";
import { GET_BULLETINS_QUERY } from "../Queries/GetBulletinsQuery";
import { UPDATE_BULLETIN_MUTATION } from "../Mutations/BulletinMutation";
import { UserContext } from "../Contexts/UserContext";
import { useQuery, useMutation } from "@apollo/client";
import ImageCarousel from "./ImageCarousel";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import { parse as parseTld } from "tldts";

import moment from "moment";

const CANDIDATE_RE =
  /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<]*)?/gi;

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    borderTopRightRadius: "10px",
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    borderTopRightRadius: "10px",
    overflowX: "hidden",
    width: theme.spacing(5) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(6) + 1,
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    backgroundColor: "#303e4f",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxHeight: "90vh",
  },
  scrollContainer: {
    overflowY: "auto",
    maxHeight: "60vh",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "50%",
    margin: "auto",
  },
  tooltipOpen: {
    whiteSpace: "normal",
    "& span": {
      fontWeight: "500",
      fontSize: "1rem",
    },
  },
  bulletinList: {
    backgroundColor: "rgb(48, 62, 79)",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",

    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  bulletinButton: {
    height: "100%",
    color: "white",
    "&:hover": {
      color: "#d9833b",
    },
  },
  bulletinTitle: {
    color: "white",
    "&:hover": {
      color: "#d9833b",
    },
  },
  high: {
    color: "red",
  },
  medium: {
    color: "orange",
  },
  low: {
    color: "green",
  },
  info: {
    color: "lightblue",
  },
  notification: {
    color: "#e926ff",
  },
  read: {
    "& span": {
      fontWeight: "normal !important",
      textDecoration: "none !important",
      fontSize: "1rem !important",
    },
  },
}));

function renderWithLinks(text) {
  const s = String(text ?? "");
  if (!s) return null;

  const nodes = [];
  let last = 0;

  s.replace(CANDIDATE_RE, (match, offset) => {
    // push text before match
    if (offset > last)
      nodes.push(<span key={`t-${last}`}>{s.slice(last, offset)}</span>);

    let candidate = match;
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(candidate);
    if (!hasScheme) candidate = "https://" + candidate;

    try {
      const url = new URL(candidate);
      const info = parseTld(url.hostname);
      const valid = info.isIp || info.isIcann; // ICANN-only; change if you want private TLDs too

      if (valid) {
        nodes.push(
          <a
            key={`a-${offset}`}
            href={candidate.replace(/^http:\/\//, "https://")}
            target="_blank"
            rel="noopener noreferrer"
          >
            {match}
          </a>
        );
      } else {
        nodes.push(<span key={`t2-${offset}`}>{match}</span>);
      }
    } catch {
      nodes.push(<span key={`t3-${offset}`}>{match}</span>);
    }

    last = offset + match.length;
    return match;
  });

  if (last < s.length) nodes.push(<span key="t-end">{s.slice(last)}</span>);
  return nodes;
}

export default function Bulletins(props) {
  const [selectedBulletin, setSelectedBulletin] = useState("");
  const [comment, setComment] = useState("");
  const classes = useStyles();
  const { open, handleDrawerClose, screenSize } = props;
  const { user, nightMode, showBulletinDrawer } = useContext(UserContext);
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
  } = useQuery(GET_BULLETINS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { officerId: user?.id, originatorId: user?.id },
    pollInterval: 300000,
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  const [bulletinMutation] = useMutation(UPDATE_BULLETIN_MUTATION);

  const handleModalOpen = id => {
    setSelectedBulletin(id);
  };

  const handleModalClose = () => {
    setSelectedBulletin(-1);
    let input = {
      userId: user?.id,
      originatorId: user?.id,
      bulletinId: selectedBulletin,
      seen: true,
      comment: "",
    };
    bulletinMutation({
      variables: {
        input,
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    setComment("");
  };

  const handleComment = (e, bulletin) => {
    e.preventDefault();
    let now = moment().format("MM/DD/YY hh:mm");
    let offlineName = `[${user?.badge}: ${
      user?.lastName
    }, ${user?.firstName.slice(0, 1)} (${
      user?.homeOriAbbreviation
    }) - ${now}] : `;
    let input = {
      userId: user?.id,
      originatorId: user?.id,
      bulletinId: selectedBulletin,
      seen: true,
      comment: comment,
      clientMutationId: "",
    };
    bulletinMutation({
      variables: {
        input,
      },
      optimisticResponse: {
        __typename: "UpdateBulletinPayload",
        id: selectedBulletin,
        clientMutationId: -1,
        updateBulletin: {
          bulletin: {
            __typename: "Bulletin",
            id: selectedBulletin,
            discussion: offlineName.concat(
              input.comment + "\n" + bulletin.discussion
            ),
            seen: true,
            clientMutationId: -1,
            details: bulletin.details,
            message: bulletin.message,
            officerId: bulletin.officerId,
            priority: bulletin.priority,
            unread: false,
            errors: [],
          },
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    setComment("");
  };

  const handleChange = e => {
    setComment(e.target.value);
  };

  const renderIcon = bulletin => {
    switch (bulletin.priority) {
      case -1:
        if (bulletin.unread) {
          return (
            <FiberManualRecord
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.notification, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        } else {
          return (
            <FiberManualRecordOutlinedIcon
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.notification, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        }
      case 0:
        if (bulletin.unread) {
          return (
            <FiberManualRecord
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.high, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        } else {
          return (
            <FiberManualRecordOutlinedIcon
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.high, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        }
      case 1:
        if (bulletin.unread) {
          return (
            <FiberManualRecord
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.medium, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        } else {
          return (
            <FiberManualRecordOutlinedIcon
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.medium, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        }
      case 2:
        if (bulletin.unread) {
          return (
            <FiberManualRecord
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.low, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        } else {
          return (
            <FiberManualRecordOutlinedIcon
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.low, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        }
      case 3:
        if (bulletin.unread) {
          return (
            <FiberManualRecord
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.info, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        } else {
          return (
            <FiberManualRecordOutlinedIcon
              style={{ fontSize: "1.25rem" }}
              className={clsx(classes.info, {
                [classes.read]: !bulletin.unread,
              })}
              fontSize={bulletin.unread ? "large" : "medium"}
            />
          );
        }
      default:
        console.log(`A bulletin priority does not exist for a listed bulletin`);
    }
  };

  if (queryError) return <div>Error loading bulletins: {queryError}</div>;

  return (
    <Drawer
      variant={
        screenSize?.width < 500 && !showBulletinDrawer
          ? "temporary"
          : "permanent"
      }
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
      data-cy="bulletin-drawer"
    >
      <div className={classes.toolbar}>
        <Tooltip title="Bulletins" placement="bottom-end">
          <Button
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerClose}
            variant="text"
            name="shrink-alerts"
            data-cy="shrink-alerts-button"
            fullWidth
            className={classes.bulletinButton}
            style={{}}
          >
            {open ? (
              <span
                style={{
                  transitionTimingFunction: "ease",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {" "}
                <div></div> <p>Bulletins</p> <ChevronLeft />{" "}
              </span>
            ) : (
              <ChevronRight />
            )}
          </Button>
        </Tooltip>
      </div>
      <Divider />
      <List className={classes.bulletinList}>
        {!queryLoading &&
          queryData?.getBulletins?.map((bulletin) => (
            <span key={bulletin.id}>
              <ListItem
                className={classes.bulletinTitle}
                button
                onClick={() => handleModalOpen(bulletin.id)}
                key={bulletin.id}
              >
                <Tooltip title={bulletin.message}>
                  <ListItemIcon>{renderIcon(bulletin)}</ListItemIcon>
                </Tooltip>
                <ListItemText
                  className={clsx(
                    { [classes.tooltipOpen]: open },
                    { [classes.read]: !bulletin.unread }
                  )}
                  primary={bulletin.message}
                />
                {bulletin.photos?.length > 0 && (
                  <CameraAltIcon style={{ color: "white", size: "0.5rem" }} />
                )}
              </ListItem>
              <Modal
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
                open={selectedBulletin === bulletin.id}
                onClose={handleModalClose}
                className={classes.modal}
                style={{ maxHeight: "75vh" }}
              >
                <div
                  style={{
                    width: "50vw",
                    overflowY: "auto",
                    color: nightMode ? "white" : "black",
                  }}
                  className={classes.paper}
                >
                  <Typography variant="h5">{bulletin.message}</Typography>
                  <br />
                  <Divider
                    style={{
                      backgroundColor: nightMode ? "white" : "black",
                      height: "0.5px",
                    }}
                  ></Divider>
                  <br></br>
                  {/* <Typography variant="span">
                    Details:{" "}
                    {bulletin.details.split("\n").map((detail, index) => (
                      <p key={index}>{renderWithLinks(detail)}</p>
                    ))}
                  </Typography> */}
                  <Typography component="div" variant="body2">
                    <strong>Details:</strong>{" "}
                    {String(bulletin?.details || "")
                      .split("\n")
                      .map((detail, index) => (
                        <Typography key={index} component="p" variant="body2">
                          {renderWithLinks(detail)}
                        </Typography>
                      ))}
                  </Typography>

                  {bulletin?.photos?.length > 0 && (
                    <div
                      style={{
                        border: "1px solid rgba(0, 0, 0, 0.23)",
                        padding: "1rem",
                        borderRadius: "4px",
                      }}
                    >
                      <ImageCarousel
                        imageArr={bulletin?.photos}
                        reverse={true}
                      />
                    </div>
                  )}
                  {bulletin.discussion ? (
                    <div className={classes.scrollContainer}>
                      Discussion: <br></br>
                      {bulletin.discussion.split("\n").map((comment, index) => (
                        <p key={index}>{comment}</p>
                      ))}
                    </div>
                  ) : (
                    <div></div>
                  )}

                  <form
                    onSubmit={e => handleComment(e, bulletin)}
                    autoComplete="off"
                  >
                    <TextField
                      id="outlined-multiline-static"
                      label="Comment"
                      multiline
                      rows="4"
                      placeholder="Add a comment"
                      variant="outlined"
                      fullWidth
                      value={comment}
                      onChange={handleChange}
                      style={{ margin: "1rem 0" }}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          handleComment(e, bulletin);
                        }
                      }}
                    />
                    <div
                      style={{ display: "flex", justifyContent: "flex-start" }}
                    >
                      <Button
                        style={{
                          backgroundColor: "#5097d6",
                          color: "white",
                          margin: "0.5rem 1rem 0.5rem 0rem",
                        }}
                        type="submit"
                        variant="contained"
                      >
                        Submit
                      </Button>
                      <Button
                        style={{
                          backgroundColor: "#5097d6",
                          color: "white",
                          margin: "0.5rem 1rem 0.5rem 0rem",
                        }}
                        onClick={handleModalClose}
                        variant="contained"
                      >
                        Close
                      </Button>
                    </div>
                  </form>
                </div>
              </Modal>
            </span>
          ))}
      </List>
    </Drawer>
  );
}
